import React, { useState } from 'react';
import { formatFileSize, downloadFile, getFileIcon, getFileTypeColor } from '../../utils/mediaHelpers';

const DocumentMessage = ({ media, caption, isSender }) => {
  const [showPreview, setShowPreview] = useState(false);

  const handleDownload = () => {
    downloadFile(media.url, media.originalName);
  };

  const handlePreview = () => {
    if (media.mimeType === 'application/pdf') {
      setShowPreview(true);
    } else {
      handleDownload();
    }
  };

  const closePreview = () => {
    setShowPreview(false);
  };

  const getDocumentType = () => {
    if (media.mimeType === 'application/pdf') return 'PDF Document';
    if (media.mimeType.includes('word')) return 'Word Document';
    if (media.mimeType.includes('excel')) return 'Excel Document';
    if (media.mimeType.includes('powerpoint') || media.mimeType.includes('presentation')) return 'PowerPoint';
    if (media.mimeType.startsWith('text/')) return 'Text File';
    return 'Document';
  };

  const getFileExtension = () => {
    return media.originalName.split('.').pop()?.toUpperCase() || 'FILE';
  };

  return (
    <>
      <div className={`media-message document-message ${isSender ? 'sender' : 'receiver'}`}>
        <div className="media-content">
          <div className="document-card" onClick={handlePreview}>
            {/* Document icon */}
            <div 
              className="document-icon"
              style={{ backgroundColor: getFileTypeColor(media.mimeType) }}
            >
              <span className="document-icon-text">
                {getFileIcon(media.mimeType)}
              </span>
              <span className="document-extension">
                {getFileExtension()}
              </span>
            </div>

            {/* Document info */}
            <div className="document-info">
              <div className="document-name">
                {media.originalName}
              </div>
              <div className="document-details">
                <span className="document-type">{getDocumentType()}</span>
                <span className="document-size">{formatFileSize(media.size)}</span>
              </div>
            </div>

            {/* Download button */}
            <button 
              className="document-download-btn"
              onClick={(e) => {
                e.stopPropagation();
                handleDownload();
              }}
              title="Download document"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z"/>
              </svg>
            </button>
          </div>
        </div>

        {/* Timestamp only */}
        <div className="media-info">
          <div className="message-time">
            {new Date().toLocaleTimeString([], { 
              hour: '2-digit', 
              minute: '2-digit' 
            })}
          </div>
        </div>
      </div>

      {/* PDF Preview Modal */}
      {showPreview && media.mimeType === 'application/pdf' && (
        <div className="pdf-preview-overlay" onClick={closePreview}>
          <div className="pdf-preview-content" onClick={(e) => e.stopPropagation()}>
            <div className="pdf-preview-header">
              <h3>{media.originalName}</h3>
              <div className="pdf-preview-controls">
                <button 
                  className="pdf-download-btn"
                  onClick={handleDownload}
                  title="Download PDF"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z"/>
                  </svg>
                  Download
                </button>
                <button 
                  className="pdf-close-btn"
                  onClick={closePreview}
                  title="Close preview"
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                  </svg>
                </button>
              </div>
            </div>
            
            <div className="pdf-preview-container">
              <iframe
                src={media.url}
                className="pdf-iframe"
                title={`PDF Preview: ${media.originalName}`}
                width="100%"
                height="100%"
              />
              <div className="pdf-fallback">
                <p>Unable to display PDF preview.</p>
                <button 
                  className="pdf-download-fallback"
                  onClick={handleDownload}
                >
                  Download PDF
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Generic Document Preview Modal for non-PDF files */}
      {showPreview && media.mimeType !== 'application/pdf' && (
        <div className="document-preview-overlay" onClick={closePreview}>
          <div className="document-preview-content" onClick={(e) => e.stopPropagation()}>
            <div className="document-preview-header">
              <h3>{media.originalName}</h3>
              <div className="document-preview-controls">
                <button 
                  className="document-download-btn"
                  onClick={handleDownload}
                  title="Download document"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z"/>
                  </svg>
                  Download
                </button>
                <button 
                  className="document-close-btn"
                  onClick={closePreview}
                  title="Close preview"
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                  </svg>
                </button>
              </div>
            </div>
            
            <div className="document-preview-body">
              <div className="document-preview-icon">
                <div 
                  className="preview-icon-large"
                  style={{ backgroundColor: getFileTypeColor(media.mimeType) }}
                >
                  <span className="preview-icon-text">
                    {getFileIcon(media.mimeType)}
                  </span>
                  <span className="preview-extension">
                    {getFileExtension()}
                  </span>
                </div>
              </div>
              
              <div className="document-preview-info">
                <h4>{getDocumentType()}</h4>
                <p>File size: {formatFileSize(media.size)}</p>
                <p>This file type cannot be previewed in the browser.</p>
                <p>Click "Download" to save the file to your device.</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DocumentMessage;
